import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import type {
  DateSelectArg,
  EventClickArg,
  EventDropArg,
} from '@fullcalendar/core';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Repeat,
  Search,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ExportMenu } from '../components/ExportMenu';
import { LiveNowStrip } from '../components/LiveNowStrip';
import { Topbar } from '../components/Topbar';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Empty } from '../components/ui/empty';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { useToast } from '../components/ui/toast';
import { useChannels } from '../lib/api/channels';
import { exportSchedule } from '../lib/api/export';
import {
  useAddSlot,
  useDeleteSlot,
  usePublicProgrammes,
  useUpdateSlot,
} from '../lib/api/programmes';
import { useWeekSchedule } from '../lib/api/schedule';
import { thisMondayLagosIso } from '../lib/date';
import { useLocaleStore } from '../lib/locales';
import type { SlotInput } from '../lib/types';
import { cn } from '../lib/utils';

/**
 * Africa/Lagos is UTC+01:00 year-round (no DST). Composing a date with the
 * fixed offset is correct and avoids depending on the user's browser TZ.
 */
const LAGOS_OFFSET = '+01:00';

/** 0=Mon … 6=Sun — matches the SlotInput.dayOfWeek convention used by the API. */
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

type CalendarView = 'timeGridWeek' | 'timeGridDay' | 'dayGridMonth';

function lagosDateTime(date: string, hms: string): string {
  const padded = /^\d{1,2}:\d{2}$/.test(hms) ? `${hms}:00` : hms;
  return `${date}T${padded}${LAGOS_OFFSET}`;
}

/**
 * Parse a FullCalendar ISO string (e.g. "2026-05-05T14:00:00+01:00") into the
 * slot-model fields: `dayOfWeek` (0=Mon…6=Sun) and `startTime` ("HH:MM:SS").
 *
 * FullCalendar v6 with `timeZone: 'Africa/Lagos'` guarantees that `startStr` /
 * `endStr` carry the Lagos offset (+01:00) — the hour/minute values in the
 * string are already Lagos wall-clock. Parsing the string directly is the only
 * approach that is correct regardless of the browser's local timezone and of
 * how FC internally manipulates its Date milliseconds.
 *
 * Day-of-week is derived from the date component of the string via a noon-UTC
 * anchor (avoids any DST or midnight-boundary ambiguity).
 */
function parseSlotFromStr(
  startStr: string,
  endStr?: string,
): { dayOfWeek: number; startTime: string; dur: number } | null {
  const m = startStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, year, month, day, hh, mm] = m as [string, string, string, string, string, string];

  const jsDay = new Date(`${year}-${month}-${day}T12:00:00Z`).getUTCDay(); // 0=Sun
  const dayOfWeek = (jsDay + 6) % 7; // 0=Mon…6=Sun

  let dur = 30;
  if (endStr) {
    const startMs = new Date(startStr).getTime();
    const endMs = new Date(endStr).getTime();
    if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
      dur = Math.max(5, Math.round((endMs - startMs) / 60_000));
    }
  }

  return { dayOfWeek, startTime: `${hh}:${mm}:00`, dur };
}

/** Round 'HH:MM:SS' or 'HH:MM' down to 'HH:MM'. */
function trimTime(t: string): string {
  return t.length >= 5 ? t.slice(0, 5) : t;
}

// ─────────────────────────────────────────────────────────────────────────
// Dialog state machine
// ─────────────────────────────────────────────────────────────────────────

type DialogMode =
  | null
  | {
      kind: 'create';
      dayOfWeek: number;
      startTime: string; // HH:MM
      durationMinutes: number;
    }
  | {
      kind: 'edit';
      slotId: string;
      programmeId: string;
      programmeSlug: string;
      programmeTitle: string;
      dayOfWeek: number;
      startTime: string;
      durationMinutes: number;
    };

// ─────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────

export default function Schedule(): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const channels = useChannels();
  const [channelSlug, setChannelSlug] = useState<string>('');
  const [weekStart, setWeekStart] = useState<string>(thisMondayLagosIso());
  const [dialog, setDialog] = useState<DialogMode>(null);
  const [view, setView] = useState<CalendarView>('timeGridWeek');
  const [viewTitle, setViewTitle] = useState<string>('');
  const calRef = useRef<FullCalendar | null>(null);

  const effectiveSlug = channelSlug || channels.data?.items[0]?.slug || '';
  const week = useWeekSchedule(effectiveSlug || null, locale, weekStart);

  const updateSlot = useUpdateSlot();
  const deleteSlot = useDeleteSlot();
  const { showToast } = useToast();

  const events = useMemo(() => {
    if (!week.data || !Array.isArray(week.data.days)) return [];
    return week.data.days.flatMap((d) =>
      d.entries.map((e) => {
        const start = lagosDateTime(d.date, e.startTime);
        const sameDayEnd = lagosDateTime(d.date, e.endTime);
        const end =
          new Date(sameDayEnd).getTime() <= new Date(start).getTime()
            ? lagosDateTime(addDays(d.date, 1), e.endTime)
            : sameDayEnd;
        return {
          id: `${e.slotId}-${d.date}`,
          title: e.title,
          start,
          end,
          extendedProps: {
            slotId: e.slotId,
            programmeId: e.programmeId,
            programmeSlug: e.programmeSlug,
            programmeTitle: e.title,
            imageUrl: e.imageUrl,
            durationMinutes: e.durationMinutes,
          },
        };
      }),
    );
  }, [week.data]);

  // ── handlers ─────────────────────────────────────────────────────────

  function callApi(fn: 'prev' | 'next' | 'today'): void {
    const api = calRef.current?.getApi();
    if (!api) return;
    api[fn]();
  }

  function changeView(next: CalendarView): void {
    setView(next);
    calRef.current?.getApi().changeView(next);
  }

  /** Drag/resize on the calendar — patches the underlying slot. Reverts on failure. */
  async function handleSlotMutation(
    info: EventDropArg | EventResizeDoneArg,
    actionLabel: 'Moved' | 'Resized',
  ): Promise<void> {
    const ext = info.event.extendedProps as { slotId?: string; programmeId?: string };
    if (!ext.slotId || !ext.programmeId) {
      info.revert();
      return;
    }

    const isResize = actionLabel === 'Resized';
    const parsed = parseSlotFromStr(info.event.startStr, info.event.endStr);
    if (!parsed) {
      info.revert();
      return;
    }
    const { dayOfWeek, startTime, dur } = parsed;

    const input: Partial<{ dayOfWeek: number; startTime: string; durationMinutes: number }> = isResize
      ? { durationMinutes: dur }
      : { dayOfWeek, startTime, durationMinutes: dur };

    try {
      await updateSlot.mutateAsync({
        programmeId: ext.programmeId,
        slotId: ext.slotId,
        input,
      });
      showToast({
        title: `${actionLabel} slot`,
        description: isResize
          ? `Duration set to ${dur} min — applies every week.`
          : `Now ${DAY_LABELS[dayOfWeek]} ${startTime.slice(0, 5)} · ${dur} min — applies every week.`,
        variant: 'success',
      });
    } catch (err) {
      info.revert();
      showToast({
        title: `${actionLabel === 'Moved' ? 'Move' : 'Resize'} failed`,
        description: (err as Error).message,
        variant: 'danger',
      });
    }
  }

  function handleSelect(arg: DateSelectArg): void {
    if (!effectiveSlug) {
      showToast({ title: 'Pick a channel first', variant: 'danger' });
      arg.view.calendar.unselect();
      return;
    }
    const parsed = parseSlotFromStr(arg.startStr, arg.endStr);
    if (!parsed) return;
    setDialog({ kind: 'create', dayOfWeek: parsed.dayOfWeek, startTime: parsed.startTime, durationMinutes: parsed.dur });
    arg.view.calendar.unselect();
  }

  function handleEventClick(arg: EventClickArg): void {
    const ext = arg.event.extendedProps as {
      slotId?: string;
      programmeId?: string;
      programmeSlug?: string;
      programmeTitle?: string;
    };
    if (!ext.slotId || !ext.programmeId) return;
    const parsed = parseSlotFromStr(arg.event.startStr, arg.event.endStr);
    if (!parsed) return;
    setDialog({
      kind: 'edit',
      slotId: ext.slotId,
      programmeId: ext.programmeId,
      programmeSlug: ext.programmeSlug ?? '',
      programmeTitle: ext.programmeTitle ?? arg.event.title,
      dayOfWeek: parsed.dayOfWeek,
      startTime: parsed.startTime,
      durationMinutes: parsed.dur,
    });
  }

  // ── render ───────────────────────────────────────────────────────────

  return (
    <>
      <Topbar title="Schedule" subtitle={`Week of ${weekStart} · ${locale.toUpperCase()}`} />

      {/* Page lives inside a fixed viewport so the calendar can flex-fill the rest. */}
      {/* Viewport minus Topbar (3.5rem) AND the global NewsTicker (2.75rem).
          When the ticker auto-hides for empty locales, the calendar sits 44px
          higher than its frame — minor cosmetic gap, not a layout break. */}
      <div className="flex h-[calc(100vh-3.5rem-2.75rem)] min-h-0 flex-col bg-surface-muted">
        {/* ─── Mission-control bar ─────────────────────────────────────── */}
        <div className="rail-accent flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-line bg-surface px-5 py-2.5">
          {/* Week display + nav */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-0.5 rounded-md border border-line bg-surface-muted/60 p-0.5">
              <button
                type="button"
                onClick={() => callApi('prev')}
                aria-label="Previous"
                className="grid h-7 w-7 place-items-center rounded-[5px] text-ink-muted transition-colors hover:bg-surface hover:text-ink"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => callApi('today')}
                className="px-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-muted transition-colors hover:text-ink"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => callApi('next')}
                aria-label="Next"
                className="grid h-7 w-7 place-items-center rounded-[5px] text-ink-muted transition-colors hover:bg-surface hover:text-ink"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="flex items-baseline gap-2.5">
              <h2 className="font-heading text-[18px] font-semibold leading-none tracking-[-0.014em] text-ink tabular">
                {viewTitle || '—'}
              </h2>
              <span className="hidden font-mono text-[10px] uppercase tracking-[0.14em] text-ink-subtle md:inline">
                Africa/Lagos · {locale.toUpperCase()}
              </span>
            </div>
          </div>

          {/* View segmented control */}
          <div className="flex items-center rounded-md border border-line bg-surface-muted/60 p-0.5">
            {(
              [
                ['timeGridWeek', 'Week'],
                ['timeGridDay', 'Day'],
                ['dayGridMonth', 'Month'],
              ] as const
            ).map(([v, label]) => {
              const active = view === v;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => changeView(v)}
                  className={cn(
                    'rounded-[5px] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors',
                    active
                      ? 'bg-ink text-white shadow-[0_1px_2px_rgba(19,20,58,0.18)]'
                      : 'text-ink-subtle hover:text-ink',
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Right cluster pushes against the edge */}
          <div className="ml-auto flex items-center gap-2.5">
            {events.length > 0 ? (
              <span className="hidden items-center gap-1.5 rounded-full bg-brand/8 px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-brand md:inline-flex">
                <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                {events.length} slot{events.length !== 1 ? 's' : ''}
              </span>
            ) : null}

            <Select value={effectiveSlug} onValueChange={setChannelSlug}>
              <SelectTrigger className="h-8 w-[160px] text-[12.5px]">
                <SelectValue placeholder="Channel" />
              </SelectTrigger>
              <SelectContent>
                {channels.data?.items.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <ExportMenu
              size="sm"
              options={[
                {
                  format: 'csv',
                  label: 'CSV (this week)',
                  description: 'One row per slot for the visible week',
                  run: () =>
                    exportSchedule({
                      format: 'csv',
                      channel: effectiveSlug,
                      locale,
                      weekStart,
                    }),
                },
                {
                  format: 'ics',
                  label: 'iCalendar (this week)',
                  description: 'Drop into Google Calendar / Outlook',
                  run: () =>
                    exportSchedule({
                      format: 'ics',
                      channel: effectiveSlug,
                      locale,
                      weekStart,
                    }),
                },
              ]}
            />
          </div>
        </div>

        {/* ─── Live strip — auto-hides when nothing on air ─────────────── */}
        <ThinLiveStrip locale={locale} />

        {/* ─── Calendar fills the remaining viewport ───────────────────── */}
        {!effectiveSlug ? (
          <div className="flex-1 px-5 py-6">
            <Empty
              title="Pick a channel"
              description="Add a channel first or select one to load its schedule."
            />
          </div>
        ) : week.isLoading && !week.data ? (
          <div className="grid flex-1 place-items-center">
            <Spinner label="Loading schedule…" />
          </div>
        ) : (
          <div className="schedule-canvas min-h-0 flex-1 px-3 pb-3 pt-2">
            <div className="schedule-shell relative h-full overflow-hidden rounded-xl border border-line bg-surface shadow-card">
              <FullCalendar
                ref={calRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="timeGridWeek"
                firstDay={1}
                initialDate={weekStart}
                timeZone={'Africa/Lagos'}
                headerToolbar={false}
                datesSet={(arg) => {
                  const yyyy = arg.start.getFullYear();
                  const mm = String(arg.start.getMonth() + 1).padStart(2, '0');
                  const dd = String(arg.start.getDate()).padStart(2, '0');
                  const next = `${yyyy}-${mm}-${dd}`;
                  if (next !== weekStart) setWeekStart(next);
                  if (arg.view.type !== view) setView(arg.view.type as CalendarView);
                  setViewTitle(arg.view.title);
                }}
                allDaySlot={false}
                slotMinTime="00:00:00"
                slotMaxTime="24:00:00"
                nowIndicator
                expandRows
                stickyHeaderDates
                height="100%"
                events={events}
                dayHeaderContent={(arg) => {
                  const weekday = arg.date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    timeZone: 'Africa/Lagos',
                  });
                  const dayNum = arg.date.toLocaleDateString('en-US', {
                    day: 'numeric',
                    timeZone: 'Africa/Lagos',
                  });
                  return (
                    <div className="flex flex-col items-center gap-0.5 py-2">
                      <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.18em] text-ink-subtle">
                        {weekday}
                      </span>
                      <span
                        className={cn(
                          'font-heading text-[15px] font-semibold leading-none tabular',
                          arg.isToday ? 'text-brand' : 'text-ink',
                        )}
                      >
                        {dayNum}
                      </span>
                    </div>
                  );
                }}
                editable
                eventStartEditable
                eventDurationEditable
                selectable
                selectMirror
                selectOverlap={false}
                eventOverlap={false}
                snapDuration="00:05:00"
                slotDuration="00:30:00"
                slotLabelFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                }}
                select={handleSelect}
                eventClick={handleEventClick}
                eventDrop={(info) => void handleSlotMutation(info, 'Moved')}
                eventResize={(info) => void handleSlotMutation(info, 'Resized')}
                eventContent={(arg) => {
                  if (arg.view.type.startsWith('dayGrid')) return undefined;
                  const dur = (arg.event.extendedProps as { durationMinutes?: number }).durationMinutes ?? 0;
                  // A 30-min slot is only ~40px tall — there's no room for the
                  // separate time header AND a readable title. The grid position
                  // already encodes the time, so for compact events we hide the
                  // header and prefix the title with a small inline time chip.
                  const compact = dur < 45;
                  if (compact) {
                    return (
                      <div className="flex h-full w-full items-start gap-1.5 overflow-hidden px-2 py-0.5">
                        <span className="mt-px shrink-0 font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-brand tabular">
                          {arg.timeText}
                        </span>
                        <span className="line-clamp-2 font-heading text-[11.5px] font-semibold leading-[1.15] tracking-[-0.005em] text-ink">
                          {arg.event.title}
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div className="flex h-full w-full flex-col overflow-hidden px-2 py-1">
                      <div className="mb-0.5 flex items-center gap-1.5">
                        <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-brand tabular">
                          {arg.timeText}
                        </span>
                        {dur ? (
                          <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-brand/55">
                            · {dur}m
                          </span>
                        ) : null}
                      </div>
                      <div className="line-clamp-2 font-heading text-[12px] font-semibold leading-[1.15] tracking-[-0.005em] text-ink">
                        {arg.event.title}
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ─── Slot dialog (create + edit) ─────────────────────────────── */}
      <SlotDialog
        mode={dialog}
        onClose={() => setDialog(null)}
        channelSlug={effectiveSlug}
        locale={locale}
        onDelete={async (programmeId, slotId) => {
          try {
            await deleteSlot.mutateAsync({ programmeId, slotId });
            setDialog(null);
            showToast({ title: 'Slot deleted', variant: 'success' });
          } catch (err) {
            showToast({
              title: 'Delete failed',
              description: (err as Error).message,
              variant: 'danger',
            });
          }
        }}
        onSaveEdit={async (programmeId, slotId, input) => {
          try {
            await updateSlot.mutateAsync({ programmeId, slotId, input });
            setDialog(null);
            showToast({ title: 'Slot updated', variant: 'success' });
          } catch (err) {
            showToast({
              title: 'Update failed',
              description: (err as Error).message,
              variant: 'danger',
            });
          }
        }}
      />
    </>
  );
}

function addDays(yyyymmdd: string, n: number): string {
  const [y, m, d] = yyyymmdd.split('-').map((p) => Number.parseInt(p, 10)) as [
    number,
    number,
    number,
  ];
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────────
// ThinLiveStrip — slim wrapper over LiveNowStrip that hides itself entirely
// when there's nothing on air. Renders a single hairline row above the grid.
// ─────────────────────────────────────────────────────────────────────────

function ThinLiveStrip({ locale }: { locale: string }): JSX.Element | null {
  // The inline LiveNowStrip already returns null when no programmes are
  // configured for YouTube. When configured but nothing is live, every row
  // is null too — the wrapper would render an empty grid. We keep the chrome
  // minimal so an empty wrapper is essentially invisible.
  return (
    <div className="schedule-live-strip border-b border-line/70 bg-surface px-5 py-1.5 empty:hidden">
      <div className="flex items-center gap-3">
        <span className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-subtle">
          <span className="live-dot" /> On air
        </span>
        <div className="min-w-0 flex-1">
          <LiveNowStrip locale={locale} inline />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SlotDialog — shared create + edit UI
// ─────────────────────────────────────────────────────────────────────────

interface SlotDialogProps {
  mode: DialogMode;
  channelSlug: string;
  locale: string;
  onClose: () => void;
  onDelete: (programmeId: string, slotId: string) => Promise<void>;
  onSaveEdit: (
    programmeId: string,
    slotId: string,
    input: Partial<SlotInput>,
  ) => Promise<void>;
}

function SlotDialog({
  mode,
  channelSlug,
  locale,
  onClose,
  onDelete,
  onSaveEdit,
}: SlotDialogProps): JSX.Element {
  const open = mode !== null;
  const isEdit = mode?.kind === 'edit';

  const [dayOfWeek, setDayOfWeek] = useState<number>(0);
  const [startTime, setStartTime] = useState<string>('09:00');
  const [duration, setDuration] = useState<number>(30);
  const [programmeId, setProgrammeId] = useState<string>('');
  const [programmeTitle, setProgrammeTitle] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const modeKey =
    mode?.kind === 'edit'
      ? `edit:${mode.slotId}`
      : mode?.kind === 'create'
        ? `create:${mode.dayOfWeek}:${mode.startTime}:${mode.durationMinutes}`
        : '';
  useEffect(() => {
    if (!mode) return;
    setDayOfWeek(mode.dayOfWeek);
    setStartTime(mode.startTime);
    setDuration(mode.durationMinutes);
    setSearch('');
    if (mode.kind === 'edit') {
      setProgrammeId(mode.programmeId);
      setProgrammeTitle(mode.programmeTitle);
    } else {
      setProgrammeId('');
      setProgrammeTitle('');
    }
    // The derived `modeKey` already encodes every field we care about; checking
    // it alone keeps the effect from re-firing on unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeKey]);

  const programmesQ = usePublicProgrammes({ locale, page: 1, pageSize: 200 });
  const filteredProgrammes = useMemo(() => {
    const items = programmesQ.data?.items ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return items.slice(0, 50);
    return items.filter((p) => p.title.toLowerCase().includes(q)).slice(0, 50);
  }, [programmesQ.data, search]);

  const addSlot = useAddSlot();
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!mode) return;
    if (busy) return;
    setBusy(true);
    try {
      if (mode.kind === 'create') {
        if (!programmeId) {
          showToast({ title: 'Pick a programme', variant: 'danger' });
          return;
        }
        const input: SlotInput = {
          channelSlug,
          locale,
          dayOfWeek,
          startTime: `${trimTime(startTime)}:00`,
          durationMinutes: duration,
          isActive: true,
        };
        await addSlot.mutateAsync({ programmeId, input });
        showToast({ title: 'Slot added', variant: 'success' });
        onClose();
      } else {
        await onSaveEdit(mode.programmeId, mode.slotId, {
          dayOfWeek,
          startTime: `${trimTime(startTime)}:00`,
          durationMinutes: duration,
        });
      }
    } catch (err) {
      showToast({
        title: isEdit ? 'Update failed' : 'Add failed',
        description: (err as Error).message,
        variant: 'danger',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <p className="eyebrow">
            <Repeat className="h-3 w-3" />
            Recurring weekly slot
          </p>
          <DialogTitle>{isEdit ? 'Edit slot' : 'Add slot'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Changes apply every week from now on (Africa/Lagos time).'
              : 'Pick a programme; the time below is editable. Slot repeats every week until removed.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-4">
          {/* Programme */}
          {isEdit && mode?.kind === 'edit' ? (
            <div>
              <Label>Programme</Label>
              <div className="mt-1.5 flex items-center gap-3 rounded-xl border border-line bg-surface-tinted px-3 py-2.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/8 text-[13px] font-bold text-brand">
                  {mode.programmeTitle.charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">{mode.programmeTitle}</p>
                  {mode.programmeSlug ? (
                    <a
                      href={`/programmes/${mode.programmeSlug}`}
                      className="text-[11.5px] font-medium text-brand hover:underline"
                    >
                      Open programme →
                    </a>
                  ) : null}
                </div>
              </div>
              <p className="mt-1.5 text-[11.5px] text-ink-subtle">
                To assign a different programme, delete this slot and add a new one.
              </p>
            </div>
          ) : (
            <div>
              <Label>Programme</Label>
              <div className="relative mt-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-subtle" />
                <Input
                  type="search"
                  className="pl-9"
                  placeholder={
                    programmesQ.isLoading
                      ? 'Loading programmes…'
                      : 'Search programmes…'
                  }
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="mt-2 max-h-44 overflow-y-auto rounded-md border border-line">
                {filteredProgrammes.length === 0 ? (
                  <p className="px-3 py-3 text-xs text-ink-subtle">
                    {programmesQ.isLoading ? 'Loading…' : 'No matching programmes.'}
                  </p>
                ) : (
                  <ul className="divide-y divide-line-hair">
                    {filteredProgrammes.map((p) => {
                      const selected = p.id === programmeId;
                      return (
                        <li key={p.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setProgrammeId(p.id);
                              setProgrammeTitle(p.title);
                            }}
                            className={cn(
                              'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors',
                              selected ? 'bg-brand-soft' : 'hover:bg-surface-tinted',
                            )}
                          >
                            {p.imageUrl ? (
                              <img
                                src={p.imageUrl}
                                alt=""
                                className="h-8 w-8 shrink-0 rounded-md object-cover"
                              />
                            ) : (
                              <span
                                className={cn(
                                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[11px] font-bold',
                                  selected ? 'bg-brand/15 text-brand' : 'bg-surface-muted text-ink-subtle',
                                )}
                              >
                                {p.title.charAt(0).toUpperCase()}
                              </span>
                            )}
                            <div className="min-w-0 flex-1">
                              <p
                                className={cn(
                                  'truncate text-[13px] font-medium leading-tight',
                                  selected ? 'text-brand' : 'text-ink',
                                )}
                              >
                                {p.title}
                              </p>
                              {p.summary ? (
                                <p className="truncate text-[11px] leading-tight text-ink-subtle">
                                  {p.summary}
                                </p>
                              ) : null}
                            </div>
                            {selected ? (
                              <Check className="h-4 w-4 shrink-0 text-brand" />
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Selected-programme confirmation (create mode only) */}
          {!isEdit && programmeId ? (
            <div className="-mt-1 flex items-center gap-2 rounded-lg border border-brand/20 bg-brand-soft px-3 py-2">
              <Check className="h-3.5 w-3.5 shrink-0 text-brand" />
              <p className="text-[12.5px] text-brand">
                <span className="font-semibold">{programmeTitle}</span> selected
              </p>
            </div>
          ) : null}

          {/* Day / start time / duration */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Day</Label>
              <Select
                value={String(dayOfWeek)}
                onValueChange={(v) => setDayOfWeek(Number.parseInt(v, 10))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAY_LABELS.map((label, idx) => (
                    <SelectItem key={label} value={String(idx)}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start (Lagos)</Label>
              <Input
                type="time"
                step={300}
                className="mt-1"
                value={trimTime(startTime)}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </div>
            <div>
              <Label>Duration (min)</Label>
              <Input
                type="number"
                min={5}
                max={24 * 60}
                step={5}
                className="mt-1"
                value={duration}
                onChange={(e) => setDuration(Math.max(5, Number(e.target.value) || 30))}
                required
              />
            </div>
          </div>

          <DialogFooter>
            {isEdit && mode?.kind === 'edit' ? (
              <Button
                type="button"
                variant="danger"
                onClick={() => void onDelete(mode.programmeId, mode.slotId)}
                disabled={busy}
                className="sm:mr-auto"
              >
                <Trash2 className="h-4 w-4" />
                Delete slot
              </Button>
            ) : null}
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Add slot'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
